// Ending an engagement — or reopening one ended by mistake.
//
// A DIALOG OF ITS OWN, not a field in the edit form. Closing a retainer answers one
// question — "does this still expect to bill?" — and it is not something to save along
// with a title change. It is
// also reached from two places (the retainers list and the retainer screen), so it lives
// here once and both open the same dialog.
//
// NO MONEY MOVES. Nothing that computes money reads this column (the settlement query
// has no status filter at all, and profit is built from invoices and expenses), and the
// act is reversible — so the dialog asks its one question and explains nothing.
//
// COMPLETED AND CANCELLED DIFFER ONLY IN WHAT THEY SAY. Every rule that reads them
// treats them the same, because the thing both remove is the expectation of future
// billing. There is no "cancelled means write off the uncollected part" — cancelling
// stops what was coming and does nothing whatever to what already happened.
import { useState } from 'react';
import { retainersApi } from '../../api';
import he from '../../i18n/he';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { ErrorText } from '../../components/Loader';

// The two end states as a pair of choices rather than a dropdown: there are exactly two,
// and a <select> shows neither at rest.
const Choice = ({ value, current, onPick, label }) => (
  <Button
    variant={current === value ? '' : 'subtle'}
    onClick={() => onPick(value)}
  >
    {label}
  </Button>
);

export default function RetainerStatusModal({ retainer, onClose, onSaved }) {
  const closed = retainer.status !== 'active';
  // Reopening has one destination, so it needs no choice. Closing starts on neither option: which of the two it was is the whole
  // decision, and preselecting one would answer it for the partner.
  const [choice, setChoice] = useState(closed ? 'active' : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!choice) return;
    setSaving(true);
    try {
      await retainersApi.setStatus(retainer.id, choice);
      onSaved();
    } catch (e) {
      setError(e);
      setSaving(false);
    }
  };

  return (
    <Modal
      title={closed ? he.retainers.reopenRetainer : he.retainers.closeRetainer}
      onClose={onClose}
      onSave={submit}
      saveDisabled={!choice || saving}
      saveLabel={closed ? he.retainers.reopenRetainer : he.retainers.closeRetainerAction}
    >
      <p className="w-modal-lead"><strong>{retainer.title}</strong></p>

      {!closed && (
        <>
          <p>{he.retainers.closeChoice}</p>
          <div className="w-choices">
            <Choice value="completed" current={choice} onPick={setChoice} label={he.retainers.closeCompleted} />
            <Choice value="cancelled" current={choice} onPick={setChoice} label={he.retainers.closeCancelled} />
          </div>
        </>
      )}

      {error && <ErrorText error={error} />}
    </Modal>
  );
}
